import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { Grid, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { CallSession, CallSessionStatus, getCallCounterpartName, getCallHistory } from '../services/callingService';
import styles from './CallsPage.module.css';

const PAGE_SIZE = 25;

type DirectionFilter = '' | 'Inbound' | 'Outbound';

const statusKeys: Record<CallSessionStatus, string> = {
  initiating: 'statusInitiating',
  ringing: 'statusRinging',
  inProgress: 'statusInProgress',
  onHold: 'statusOnHold',
  ended: 'statusEnded',
  failed: 'statusFailed',
  cancelled: 'statusCancelled',
};

const statusFilterOptions: CallSessionStatus[] = ['ringing', 'inProgress', 'ended', 'failed', 'cancelled'];

const isInbound = (session: CallSession) => session.direction.toLowerCase() === 'inbound';

const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds < 0) {
    return '-';
  }

  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

/**
 * Call history for the workspace. Audio lives on 3CX; this is the record of what happened.
 */
export const CallsPage: React.FC = () => {
  const { t } = useTranslation(['calling', 'common']);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canSeeEveryone = hasPermission(PermissionScopes.callsReadAll);

  const [sessions, setSessions] = useState<CallSession[]>([]);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState<DirectionFilter>('');
  const [status, setStatus] = useState<string>('');
  const [includeTeam, setIncludeTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCallHistory({
        page,
        pageSize: PAGE_SIZE,
        q: search.trim() || undefined,
        direction: direction || undefined,
        status: status || undefined,
        includeTeam: canSeeEveryone && includeTeam ? true : undefined,
      });

      setSessions(result.items);
      setTotal(result.total);
      setHasNext(result.hasNext);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('historyLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, search, direction, status, includeTeam, canSeeEveryone, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(); }, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  // Any filter change starts a new result set, so never keep the old page number.
  useEffect(() => {
    setPage(1);
  }, [search, direction, status, includeTeam]);

  const openSession = (session: CallSession) => {
    if (session.conversationId) {
      navigate(`/conversations/${session.conversationId}`);
    } else if (session.contactId) {
      navigate(`/contacts/${session.contactId}`);
    }
  };

  const statusClassName = (session: CallSession) => {
    if (session.status === 'failed' || session.status === 'cancelled') {
      return `${styles.statusBadge} ${styles.statusFailed}`;
    }

    if (session.status === 'ringing' || session.status === 'inProgress' || session.status === 'onHold') {
      return `${styles.statusBadge} ${styles.statusLive}`;
    }

    return styles.statusBadge;
  };

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>{t('historyTitle')}</h2>
          <p className={styles.subtitle}>{t('historyCount', { count: total })}</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          type="text"
          className={`search-input ${styles.searchInput}`}
          placeholder={t('historySearchPlaceholder')}
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <select
          className={`form-control ${styles.filterSelect}`}
          value={direction}
          onChange={event => setDirection(event.target.value as DirectionFilter)}
          aria-label={t('historyDirection')}
        >
          <option value="">{t('historyAllDirections')}</option>
          <option value="Inbound">{t('inbound')}</option>
          <option value="Outbound">{t('outbound')}</option>
        </select>
        <select
          className={`form-control ${styles.filterSelect}`}
          value={status}
          onChange={event => setStatus(event.target.value)}
          aria-label={t('historyStatus')}
        >
          <option value="">{t('historyAllStatuses')}</option>
          {statusFilterOptions.map(option => (
            <option key={option} value={option}>{t(statusKeys[option])}</option>
          ))}
        </select>

        {canSeeEveryone ? (
          <label className={styles.scopeToggle}>
            <input
              type="checkbox"
              checked={includeTeam}
              onChange={event => setIncludeTeam(event.target.checked)}
            />
            {t('historyIncludeTeam')}
          </label>
        ) : null}
      </div>

      {error ? <div className={styles.errorState}>{error}</div> : null}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <Spinner text={t('historyLoading')} />
        </div>
      ) : sessions.length === 0 ? (
        <div className={styles.emptyState}>{t('historyEmpty')}</div>
      ) : (
        <>
          <Grid
            columns={[
              {
                key: 'direction',
                label: t('historyDirection'),
                render: (session: CallSession) => (
                  <span className={`${styles.directionBadge} ${isInbound(session) ? styles.inbound : styles.outbound}`}>
                    {isInbound(session) ? `↙ ${t('inbound')}` : `↗ ${t('outbound')}`}
                  </span>
                ),
              },
              {
                key: 'counterpart',
                label: t('historyCounterpart'),
                render: (session: CallSession) => (
                  <div>
                    <span className={styles.primaryText}>
                      {getCallCounterpartName(session) || session.contactDisplayNumber || t('unknownCaller')}
                    </span>
                    <span className={styles.secondaryText}>
                      {isInbound(session) ? session.fromNumber : session.toNumber}
                    </span>
                  </div>
                ),
              },
              ...(canSeeEveryone && includeTeam ? [{
                key: 'employee',
                label: t('historyEmployee'),
                render: (session: CallSession) => (
                  <span className={session.employeeName ? undefined : styles.mutedValue}>
                    {session.employeeName || '-'}
                  </span>
                ),
              }] : []),
              {
                key: 'status',
                label: t('historyStatus'),
                render: (session: CallSession) => (
                  <span className={statusClassName(session)}>{t(statusKeys[session.status])}</span>
                ),
              },
              {
                key: 'startedAt',
                label: t('historyStartedAt'),
                render: (session: CallSession) => (
                  <span>{session.startedAtUtc ? formatDateTime(session.startedAtUtc) : '-'}</span>
                ),
              },
              {
                key: 'duration',
                label: t('historyDuration'),
                render: (session: CallSession) => (
                  <span className={session.durationSeconds ? undefined : styles.mutedValue}>
                    {formatDuration(session.durationSeconds)}
                  </span>
                ),
              },
            ]}
            rows={sessions}
            rowKey={(session: CallSession) => session.id}
            onRowClick={openSession}
          />

          <div className={styles.pager}>
            <span>{t('historyPage', { page })}</span>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage(current => Math.max(1, current - 1))}
            >
              {t('previous', { ns: 'common', defaultValue: 'Previous' })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!hasNext}
              onClick={() => setPage(current => current + 1)}
            >
              {t('next', { ns: 'common', defaultValue: 'Next' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CallsPage;
